import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { IconDown, IconUp } from "./Icons";
import DialysisTube from "./img/DialysisTube.svg";
import { getStyle } from "./DialysisBand.utils";
import {
  DialysisDragDropContext,
  splitIDs,
  getCellTypeFromID,
  getNextClipValue,
  getPreviousClipValue,
} from "../DialysisDragDrop.utils";
import {
  DIALYSIS_TYPE,
  DIALYSIS_DRAG_DROP,
  CELL_TYPE,
} from "../DialysisDragDrop.constants";
import "../style.scss";

const slideDown = keyframes`
  from { transform: translateY(0px); }
  to { transform: translateY(390px); }
`;

const slideUp = keyframes`
  from { transform: translateY(390px); }
  to { transform: translateY(0px); }
`;

const BandDraggable = styled.div`
  &.animate-idle-down {
    animation: ${slideDown} ease-out 0.8s forwards;
  }
  &.animate-idle-up {
    animation: ${slideUp} ease-out 0.3s forwards;
  }
`;

export function DialysisBand(props) {
  const { state, clipValues, onStripChange, onDialysisBandClick } =
    React.useContext(DialysisDragDropContext);
  const style = getStyle({ state, props });
  const bandRef = useRef();

  const setDraggable = () =>
    props.dialysisType === DIALYSIS_TYPE.DRAGGABLE &&
    props.feedbackType !== "SUCCESS";

  const onDragStart = (event) => {
    event.dataTransfer.setData(
      DIALYSIS_DRAG_DROP.DIALYSIS_ID,
      props.dialysisId
    );
    event.dataTransfer.setDragImage(
      event.target,
      event.target.clientWidth / 2, // center the drag image under the cursor.
      event.target.clientHeight / 2 // center the drag image under the cursor.
    );
  };

  const { cellType } = getCellTypeFromID(props.dialysisId);

  const onUpIconChange = () => {
    const [colId, prevCellId, dialysisId] = splitIDs(props.dialysisId);
    if (clipValues[clipValues.length - 1] === props.value) {
      if (prevCellId !== 0) {
        onStripChange({
          id: props.id,
          colId,
          prevCellId,
          currentCellId: prevCellId - 1,
          dialysisId,
          value: 0,
        });
      }
    } else {
      const value = getNextClipValue(clipValues, props.value);
      onStripChange({
        id: props.id,
        colId,
        prevCellId,
        currentCellId: prevCellId,
        dialysisId,
        value,
      });
    }
  };

  const onDownIconChange = () => {
    const [colId, prevCellId, dialysisId] = splitIDs(props.dialysisId);
    if (
      cellType === CELL_TYPE.DIALYSIS_HOLDER ||
      clipValues[0] === props.value
    ) {
      if (prevCellId !== 1) {
        props.dialysisType === DIALYSIS_TYPE.DRAGGABLE &&
          onStripChange({
            id: props.id,
            colId,
            prevCellId,
            currentCellId: prevCellId + 1,
            dialysisId,
            value:
              cellType === CELL_TYPE.DIALYSIS_HOLDER
                ? clipValues[clipValues.length - 1]
                : 0,
          });
      }
    } else {
      const value = getPreviousClipValue(clipValues, props.value);
      props.dialysisType === DIALYSIS_TYPE.DRAGGABLE &&
        onStripChange({
          id: props.id,
          colId,
          prevCellId,
          currentCellId: prevCellId,
          dialysisId,
          value,
        });
    }
  };

  const isUpIcon = () =>
    props.feedbackType !== "SUCCESS" && cellType === CELL_TYPE.DIALYSIS_PATH;

  const isDownIcon = () =>
    props.feedbackType !== "SUCCESS" &&
    clipValues.toReversed()[clipValues.length - 1] !== props.value &&
    (cellType === CELL_TYPE.DIALYSIS_HOLDER ||
      cellType === CELL_TYPE.DIALYSIS_PATH);

  const onKeyUp = (event) => {
    if (event.key === "ArrowDown" && props.feedbackType !== "SUCCESS")
      onDownIconChange();
    if (event.key === "ArrowUp" && props.feedbackType !== "SUCCESS")
      onUpIconChange();
  };

  const onClick = () => {
    props.dialysisType === DIALYSIS_TYPE.DRAGGABLE &&
      onDialysisBandClick(props.dialysisId, props.id);
  };

  React.useEffect(() => {
    if (bandRef && bandRef.current && props.selected) {
      bandRef.current.focus();
    }
  }, [bandRef]);

  const setAriaLabel = () => {
    if (props.type === "DIALYSIS_HOLDER") {
      return `Band [1] to be placed. Select the band to adjust the BP by using the arrow keys.`;
    } else if (props.type === "DIALYSIS_PATH") {
      if (props.columnType === "LADDER_COLUMN") {
        return `Band [1] at ${props.value}bp`;
      } else {
        return `Band [1] at ${props.value}bp. Select the band to adjust the BP by using the arrow keys.`;
      }
    } else if (props.type === "TRASH_CAN") {
      return `Band [1] to be placed at trash. Select the band to adjust the BP by using the arrow keys.`;
    } else {
      return null;
    }
  };

  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (props.colId === 1) {
      let timer1, timer2;
      if (!props.responses) {
        if (!idle) {
          timer1 = setTimeout(() => setIdle(true), 2400);
        } else {
          timer2 = setTimeout(() => setIdle(false), 1000);
        }
      } else {
        clearTimeout(timer1);
        clearTimeout(timer2);
        setIdle(false);
      }
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [idle]);

  useEffect(() => {
    toggleIdleAnimation(idle);
  }, [idle]);

  const toggleIdleAnimation = (idle) => {
    if (!props.responses) {
      if (idle) {
        bandRef.current.classList.remove("animate-idle-up");
        bandRef.current.classList.add("animate-idle-down");
      } else {
        if (bandRef.current.classList.contains("animate-idle-down")) {
          bandRef.current.classList.remove("animate-idle-down");
          bandRef.current.classList.add("animate-idle-up");
        }
      }
    } else {
      bandRef.current.classList.remove("animate-idle-up");
      bandRef.current.classList.remove("animate-idle-down");
    }
  };

  return (
    <BandDraggable
      style={style}
      ref={bandRef}
      onKeyUp={onKeyUp}
      onKeyDown={(e) => {
        // space and arrow keys
        if (
          ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].indexOf(
            e.key
          ) > -1
        ) {
          e.preventDefault();
        }
      }}
      onDragStart={onDragStart}
      draggable={setDraggable()}
      aria-label={setAriaLabel()}
      tabIndex={props.tabIndex}
      onMouseDown={onClick}
      className={`${props.className}${props.dialysisType.toLowerCase()} ${props.selected ? "selected" : null}`}
    >
      <div className="band">
        {isUpIcon() && (
          <IconUp className="band_iconup" onClick={onUpIconChange} />
        )}
        <div
          role="button"
          tabIndex={0}
          className="band_text"
          onKeyDown={onClick}
          onClick={onClick}
        >
          <img src={DialysisTube} alt="" />
        </div>
        {isDownIcon() && (
          <IconDown className="band_icondown" onClick={onDownIconChange} />
        )}
      </div>
    </BandDraggable>
  );
}

DialysisBand.propTypes = {
  /**
   * Component id from the notebook data file
   */
  id: PropTypes.number,
  /**
   * Text to render inside DialysisStrip
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) || null,
  /**
   * Classname to be set on component
   */
  className: PropTypes.string,
  /**
   * Unique Id for Dialysis
   */
  dialysisId: PropTypes.string,
  /**
   * Type of Selected Option
   */
  selectedOption: PropTypes.object,
  /**
   * Type of Dialysis-Strip
   */
  dialysisType: PropTypes.oneOf(Object.keys(DIALYSIS_TYPE)),
  /**
   * Height value of Dialysis
   */
  height: PropTypes.number,
  /**
   * Opacity value of Dialysis
   */
  opacity: PropTypes.number,
  /**
   * Postion value of Dialysis
   */
  value: PropTypes.number || null,
  /**
   * Boolean to set if get is selected
   */
  selected: PropTypes.bool,
  /**
   * type is used to determine where is the dialysisband is displayed
   */
  type: PropTypes.string,
  /**
   * Tabindex value passed from outside the component for keyboard navigation
   */
  tabIndex: PropTypes.number,
  /**
   * ColumnType mention which typeof column
   */
  columnType: PropTypes.string,
};

DialysisBand.defaultProps = {
  selected: false,
  type: "none",
  tabIndex: 0,
};
